import React, { useContext } from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import moment from 'moment';
import ModalModule from '../modal';
import Utils from '../utils';
import { ShippingStatus, InboundStatus } from './Status';
import { PackingMethodIdMap } from './Mappings';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { Communication } = Utils;

const ConfirmInboundModal = ({ data, modalId }) => {
	const { onSuccess, editingInbound } = data;
	const { showError, closeModal } = useContext(ModalContext);

	const {
		warehouse,
		reference,
		purchaseOrderNumber,
		packagingType,
		shippingMethod,
		items,
		expectedArrivalDate,
		trackingInfo,
		fromAddress = {
			name: '',
			city: '',
			stateProvince: '',
			companyName: '',
			addressLine1: '',
			addressLine2: '',
			addressLine3: '',
			postalCode: '',
			countryCode: '',
		},
		status,
	} = editingInbound;

	let productList = '';
	const products = items.reduce((result, productItem) => {
		if (result[productItem.sku]) {
			result[productItem.sku] += productItem.quantityExpected;
		} else {
			result[productItem.sku] = productItem.quantityExpected;
		}
		return result;
	}, {});
	for (const [sku, quantity] of Object.entries(products)) {
		productList += `${sku} (${quantity} units)\n`;
	}

	let trackingList = '';
	trackingInfo.forEach(trackingItem => {
		const { trackingNumber, carrier } = trackingItem;
		if (!isEmpty(trackingNumber) || !isEmpty(carrier)) {
			trackingList += `${trackingNumber} (${carrier})\n`;
		}
	});

	const buildFromAddress = address => {
		if (isEmpty(address)) {
			return null;
		}

		return {
			name: address.name,
			city: address.city,
			state_province: address.stateProvince,
			company_name: address.companyName,
			address_line1: address.addressLine1,
			address_line2: address.addressLine2,
			address_line3: address.addressLine3,
			postal_code: address.postalCode,
			country_code: address.countryCode,
			phone: address.phone,
			email: address.email,
			residential_indicator: address.residentialIndicator,
		};
	};

	// eslint-disable-next-line no-shadow
	const formatCarriers = trackingInfo =>
		// eslint-disable-next-line no-shadow
		trackingInfo.map(trackingInfo => {
			const { trackingNumber } = trackingInfo;
			if (trackingInfo.carrier === 'DHL eCommerce') {
				return {
					...trackingInfo,
					carrier: 'DhlECommerce',
					tracking_number: trackingNumber,
				};
			}
			if (trackingInfo.carrier === 'Other') {
				return {
					...trackingInfo,
					carrier_other_name: 'Other',
					tracking_number: trackingNumber,
				};
			}
			return {
				...trackingInfo,
				tracking_number: trackingNumber,
			};
		});

	const onSubmitButtonClick = async () => {
		const { warehouseId } = editingInbound;
		const inboundRequestData = {
			from_address: buildFromAddress(fromAddress),
			warehouse_id: warehouseId,
			items,
			packaging_type: packagingType,
			tracking_info: formatCarriers(trackingInfo),
			expected_arrival_date: expectedArrivalDate
				? moment(expectedArrivalDate).format('YYYY/MM/DD')
				: moment()
						.add(1, 'y')
						.format('YYYY/MM/DD'), // Proxy value. This value will come from post-MVP work but is required
			purchase_order_number: purchaseOrderNumber,
			reference,
			number_of_box_labels: 1, // Proxy value. This value will come from post-MVP work but is required
		};
		let response;

		inboundRequestData.items.forEach(item => {
			const { productId, quantityExpected } = item;
			item.product_id = productId;
			item.quantity_expected = quantityExpected;
		});

		// TODO - add logic to update barcode for products where user manually entered values. Create/update should
		// happen once we have completed those updates.

		if (editingInbound.status === InboundStatus.Draft) {
			response = await Communication.createInbound(inboundRequestData);
		} else {
			response = await Communication.updateInbound(
				editingInbound.receivingId,
				inboundRequestData
			);
		}

		if (Communication.isSuccessful(response)) {
			if (onSuccess) {
				onSuccess();
			}
		} else {
			showError({ response });
		}

		closeModal(modalId);
	};

	const primaryButton = {
		type: ModalButtonActionType.Ok,
		text: 'Submit',
		onClick: onSubmitButtonClick,
		dataqa: 'confirm-inbound-submit-button',
	};

	const shipFrom =
		shippingMethod === ShippingStatus.Self
			? `${fromAddress.name} - ${fromAddress.city}, ${fromAddress.stateProvince}`
			: `${fromAddress.name} - ${fromAddress.companyName} \n ${fromAddress.addressLine1} \n ${fromAddress.city}, ${fromAddress.stateProvince} ${fromAddress.postalCode}`;

	const secondaryButton = {
		type: ModalButtonActionType.Cancel,
		text: 'Cancel',
		dataqa: 'confirm-inbound-cancel-button',
	};

	return (
		<Modal
			modalId={modalId}
			className="common-inbound-details confirm-inbound-modal"
			header={
				status === InboundStatus.Draft
					? 'Confirm Inbound Details'
					: 'Confirm Inbound Details Update'
			}
			primaryButton={primaryButton}
			secondaryButton={secondaryButton}
		>
			<div className="modal-content p-grid">
				<p className="p-col-12">
					Ensure the following Inbound summary looks correct. After you’ve confirmed this
					inbound, you can print box labels for your Inbound.
				</p>
				<div className="inbound-details-list">
					<div className="p-grid p-col-12">
						<p className="sub-header p-col-12">Inbound Details</p>
						<div className="p-grid p-col-12">
							<p className="confirm-modal-label p-col-4">Warehouse:</p>
							<p className="p-col-8 warehouse-details">
								{shippingMethod === ShippingStatus.Zorbit
									? `${warehouse.city}, ${warehouse.stateProvince}`
									: `${warehouse.companyName} \n ${warehouse.addressLine1} \n ${warehouse.city}, ${warehouse.stateProvince} ${warehouse.postalCode}`}
							</p>
						</div>
						<div className="p-grid p-col-12">
							<p className="confirm-modal-label p-col-4">PO #:</p>
							<p className="p-col-8">{purchaseOrderNumber}</p>
						</div>
						<div className="p-grid p-col-12">
							<p className="confirm-modal-label p-col-4">Reference:</p>
							<p className="p-col-8">{reference}</p>
						</div>
						<div className="p-grid p-col-12">
							<p className="confirm-modal-label p-col-4">Packing Method:</p>
							<p className="p-col-8">
								{packagingType === PackingMethodIdMap.Pallet ? 'Pallets' : 'Boxes'}
							</p>
						</div>
						<div className="p-grid p-col-12">
							<p className="confirm-modal-label p-col-4">Shipping Method:</p>
							<p className="p-col-8">
								{shippingMethod === ShippingStatus.Zorbit
									? 'Send with Zorbit (UPS)'
									: 'Send on my own'}
							</p>
						</div>
					</div>
					<div className="p-grid p-col-12">
						<p className="sub-header p-col-12">Product Details</p>
						<div className="p-grid p-col-12">
							<p className="confirm-modal-label p-col-4">Products (Qty.):</p>
							<p className="p-col-8 product-list">{productList}</p>
						</div>
					</div>
					<div className="p-grid p-col-12">
						<p className="sub-header p-col-12">Shipping Details</p>
						{shippingMethod !== ShippingStatus.Zorbit && (
							<div className="p-grid p-col-12">
								<p className="confirm-modal-label p-col-4">Estimated Arrival:</p>
								<p className="p-col-8">
									{moment(expectedArrivalDate).format('MM/DD/YYYY')}
								</p>
							</div>
						)}
						<div className="p-grid p-col-12">
							<p className="confirm-modal-label p-col-4">Ship From:</p>
							<p className="p-col-8 shipfrom">
								{isEmpty(fromAddress) ? '' : shipFrom}
							</p>
						</div>
						{shippingMethod !== ShippingStatus.Zorbit && (
							<div className="p-grid p-col-12">
								<p className="confirm-modal-label p-col-4 tracking">
									Tracking Number(s):
								</p>
								<p className="p-col-8 tracking">{trackingList}</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

ConfirmInboundModal.propTypes = {
	data: PropTypes.shape({
		onSuccess: PropTypes.func.isRequired,
		editingInbound: PropTypes.object,
	}),
};

export default ConfirmInboundModal;
